/**
 * One row per person at the table: who they are, and what they thought.
 *
 * Store-connected on purpose. Every tap writes immediately rather than
 * collecting a draft the user has to remember to save — this sheet is opened
 * with a plate still on the table and gets closed by a swipe as often as by a
 * button. A draft that needed confirming would lose exactly the answers the
 * feature exists to collect.
 *
 * Three chips, not five stars: the people being asked include children whose
 * parent is tapping for them, and "did you like it?" has three honest answers.
 */
import React, { useCallback } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import {
  setEaterVerdict,
  selectRecipeVerdicts,
  type VerdictValue,
} from '../../redux/recipeMetadataSlice';
import type { RootState } from '../../redux/store';
import { useHouseholdEaters, type TableMember } from '../../hooks/useHouseholdEaters';
import { EaterAvatar } from './EaterAvatar';
// Shared so a verdict wears the same glyph in the sheet and on the cards.
import { VERDICT_ICON as ICON } from './VerdictAvatars';
import { trackEvent } from '../../services/analyticsService';

const HIT = { top: 6, bottom: 6, left: 4, right: 4 };

const TONE: Record<VerdictValue, { fg: string; bg: string }> = {
  love: { fg: Paprika.approvalYes, bg: Paprika.approvalYesTint },
  ok: { fg: Paprika.textSecondary, bg: Paprika.bgSunk },
  no: { fg: Paprika.approvalNo, bg: Paprika.approvalNoTint },
};

const ORDER: VerdictValue[] = ['love', 'ok', 'no'];

export type VerdictSurface = 'cook_done' | 'cook_log' | 'cooked_stamp';

interface Props {
  recipeId: string;
  /** Which cook gesture opened this, for telemetry. */
  surface: VerdictSurface;
}

export function VerdictRows({ recipeId, surface }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { table } = useHouseholdEaters(t('household.eaters.selfLabel'));
  const verdicts = useSelector((s: RootState) => selectRecipeVerdicts(s, recipeId));

  const cast = useCallback(
    (member: TableMember, value: VerdictValue) => () => {
      // Re-tapping the active chip un-answers, matching ApprovalThumbs.
      const next = verdicts[member.id]?.value === value ? null : value;
      dispatch(setEaterVerdict({ recipeId, eaterId: member.id, value: next }));
      if (next) {
        trackEvent('household_verdict_set', {
          value: next,
          isSelf: member.isSelf,
          isAccount: member.isAccount,
          eaterCount: table.length,
          surface,
        });
      }
    },
    [dispatch, recipeId, verdicts, table.length, surface]
  );

  return (
    <View style={styles.rows}>
      {table.map((member) => {
        const current = verdicts[member.id]?.value;
        return (
          <View key={member.id} style={styles.row}>
            <View style={styles.who}>
              <EaterAvatar member={member} />
              <Text style={styles.name} numberOfLines={1}>
                {member.name}
              </Text>
            </View>
            <View style={styles.chips}>
              {ORDER.map((value) => {
                const active = current === value;
                const tone = TONE[value];
                return (
                  <Pressable
                    key={value}
                    onPress={cast(member, value)}
                    testID={`verdict-chip-${value}`}
                    hitSlop={HIT}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={`${member.name} — ${t(`household.verdict.${value}`)}`}
                    style={[
                      styles.chip,
                      active && { backgroundColor: tone.bg },
                    ]}
                  >
                    <Feather
                      name={ICON[value]}
                      size={13}
                      color={active ? tone.fg : Paprika.textMuted}
                    />
                    <Text
                      style={[
                        styles.chipText,
                        { color: active ? tone.fg : Paprika.textMuted },
                      ]}
                    >
                      {t(`household.verdict.${value}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  rows: {
    gap: PaprikaSpacing.sm,
  },
  // A phone-width sheet cannot fit a name and three labelled chips on one
  // line: the name used to shrink to its first letter ("A…" for Amal), which
  // made every row the same row. The name keeps a floor instead and the chips
  // wrap under it, right-aligned, when the line is too short.
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 9,
    rowGap: 6,
  },
  who: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 112,
  },
  name: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    flexShrink: 1,
    minWidth: 0,
  },
  chips: {
    flexDirection: 'row',
    gap: 4,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 9,
    borderRadius: PaprikaRadius.full,
    // Tonal surface, no outline — depth comes from the shift, not a hairline.
    backgroundColor: Paprika.surfaceLow,
  },
  chipText: {
    ...PaprikaTypography.labelSmall,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
  },
});
